import { GenericError } from '@monorepo/shared/componentsV2/errorDisplays/GenericError';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { useWorkflow } from '@monorepo/shared/hooks/workflows/useWorkflow';
import {
  MathUnit,
  WorkflowStepOperationType,
  WorkflowStepResponse,
} from 'mapistry-shared';
import React from 'react';
import styled from 'styled-components';
import { DatasetChip } from '../shared/DatasetChip';
import { getStepDisplayInfo } from './stepUtils';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  align-items: center;
  justify-content: left;
  max-width: 70rem;
  padding: 0.625rem;
  margin-top: 1rem;
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.grayf4};
  border-radius: 4px;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-shrink: 1;
  justify-content: left;
  margin-right: 0.5rem;
`;

const WorkflowStepMainInfoContainer = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-width: 2.2rem;
  height: 1.5rem;
  padding: 0 0.5rem;
  margin-right: 0.5rem;
  color: ${({ theme }) => theme.colors.gray666};
  background-color: ${({ theme }) => theme.colors.blue08};
  border-radius: 5px;

  & svg {
    height: 0.8rem;
  }
`;

const WorkflowStepNameContainer = styled.div`
  margin-left: 0.5rem;
  cursor: default;
`;

const StepConnector = styled.div`
  height: 1px;
  padding: 0 0.25rem;
  margin-left: -0.5rem;
  background-color: ${({ theme }) => theme.colors.gray666};
  opacity: 0.5;
`;

const datasetChipLabelClassName = 'workflow-step-chips__dataset-chip__label';
const StyledDatasetChip = styled(DatasetChip)`
  .${datasetChipLabelClassName} {
    white-space: nowrap;
  }
`;
const DatasetChipMargin = styled(StyledDatasetChip)`
  margin-right: 0.75rem;
  font-weight: 500;
`;

const AggregationName = styled.span`
  &:not(:first-child) {
    padding-left: 0.5rem;
    margin-left: 0.5rem;
    border-left: 1px solid ${({ theme }) => theme.colors.graybbb};
  }
`;

function getStepDisplayLabel(step: WorkflowStepResponse) {
  const { type } = step;
  switch (type) {
    case WorkflowStepOperationType.FORMULA:
      return (
        <div>{`${step.operationConfig.name}${
          step.operationConfig.outputUnits
            ? ` (${MathUnit.getLabel(step.operationConfig.outputUnits)})`
            : ''
        }`}</div>
      );
    case WorkflowStepOperationType.JOIN:
      return undefined;
    case WorkflowStepOperationType.AGGREGATION: {
      const MAX_NAME_COUNT = 2;
      const { aggregationDefinitions } = step.operationConfig;
      return (
        <div>
          {aggregationDefinitions.slice(0, MAX_NAME_COUNT).map((ad) => (
            <AggregationName key={ad.id}>{ad.name}</AggregationName>
          ))}
          {aggregationDefinitions.length > MAX_NAME_COUNT && (
            <AggregationName>
              {`+${aggregationDefinitions.length - MAX_NAME_COUNT} more`}
            </AggregationName>
          )}
        </div>
      );
    }
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(
        `Trying to render a Workflow step for an unknown step type ${exhaustiveCheck}`,
      );
    }
  }
}

function getAdditionalInfo(step: WorkflowStepResponse) {
  const { type } = step;
  switch (type) {
    case WorkflowStepOperationType.JOIN:
      return (
        <>
          <StepConnector />
          <InnerContainer>
            <StyledDatasetChip
              name={step.operationConfig.joinToDataset.name}
              type={step.operationConfig.joinToDataset.type}
              labelClassName={datasetChipLabelClassName}
            />
          </InnerContainer>
        </>
      );
    case WorkflowStepOperationType.FORMULA:
    case WorkflowStepOperationType.AGGREGATION:
      return null;
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(
        `Trying to render a Workflow step for an unknown step type ${exhaustiveCheck}`,
      );
    }
  }
}

interface WorkflowStepChipsProps {
  step: WorkflowStepResponse;
}

function WorkflowStepChips({ step }: WorkflowStepChipsProps) {
  const { icon } = getStepDisplayInfo(step);
  const label = getStepDisplayLabel(step);
  const additionalInfo = getAdditionalInfo(step);
  const tooltip =
    step.type === WorkflowStepOperationType.FORMULA
      ? step.operationConfig.friendlyExpression
      : undefined;
  const tooltipPopperProps = {
    disablePortal: false,
    popperOptions: {
      modifiers: {
        offset: {
          offset: '0, 4',
        },
      },
    },
  };

  const mainInfoContainer = (
    <WorkflowStepMainInfoContainer aria-hidden>
      {icon}
      {label && <WorkflowStepNameContainer>{label}</WorkflowStepNameContainer>}
    </WorkflowStepMainInfoContainer>
  );

  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip} popperProps={tooltipPopperProps}>
          {mainInfoContainer}
        </Tooltip>
      ) : (
        mainInfoContainer
      )}
      {additionalInfo}
    </>
  );
}

type WorkflowStepsDiagramProps = {
  organizationId: string;
  workflowId: string;
};

export function WorkflowStepsDiagram({
  organizationId,
  workflowId,
}: WorkflowStepsDiagramProps) {
  const { workflow, isLoading } = useWorkflow({
    config: { useErrorBoundary: false },
    organizationId,
    workflowId,
  });

  if (isLoading) {
    return (
      <Container>
        <Loading size="small" />
      </Container>
    );
  }

  if (!workflow) {
    return (
      <Container>
        <GenericError message="Couldn't load details to show workflow diagram" />
      </Container>
    );
  }

  return (
    <Container>
      <DatasetChipMargin
        name={workflow.inputDataset.name}
        type={workflow.inputDataset.type}
        labelClassName={datasetChipLabelClassName}
      />
      {workflow.steps.map((step) => (
        <WorkflowStepChips key={step.id} step={step} />
      ))}
    </Container>
  );
}
